import { useMutation } from '@tanstack/react-query'

import usePosthogCapture from '../../../hooks/posthogCapture/usePosthogCapture'
import { useInvalidatePanelQueries } from '../../../hooks/useInvalidatePanelQueries'
import useNotyf from '../../../hooks/useNotyf'
import { useRecommendedPartnerModal } from '../../../hooks/useRecommendedPartnerModal'
import { useShowGenericServerError } from '../../../hooks/useShowGenericServerError'
import { API_VIOLATION_CODE } from '../../../lib/constants'
import constraintViolationService from '../../../lib/services/constraintViolationService'
import errorTypeService from '../../../lib/services/errorTypeService'
import recommendedPartnerService from '../../../lib/services/recommendedPartnerService'

interface MutationVariables {
  partnerId: number
  partnerName: string
}

export function useAcceptRecommendedPartnerMutation() {
  const notyf = useNotyf()
  const invalidatePanelQueries = useInvalidatePanelQueries()
  const recommendedPartnerModal = useRecommendedPartnerModal()
  const { posthogCapture } = usePosthogCapture()
  const showGenericServerError = useShowGenericServerError()

  return useMutation({
    mutationFn: (variables: MutationVariables) =>
      recommendedPartnerService.acceptRecommendedPartner(variables.partnerId),
    onSuccess: async (_data, variables) => {
      await invalidatePanelQueries()
      recommendedPartnerModal.hide()
      notyf.success(
        `${variables.partnerName} has been added to your professional network`,
      )

      posthogCapture({
        appArea: 'relationship-management',
        action: 'vetted-relationship-added',
      })
    },
    onError: (error) => {
      if (errorTypeService.isConstraintViolationListError(error)) {
        const relationshipExistsError =
          constraintViolationService.getViolationByCode(
            error,
            API_VIOLATION_CODE.relationshipExists,
          )

        if (relationshipExistsError) {
          notyf.open({
            type: 'info',
            message: relationshipExistsError.message,
            duration: 6000,
          })

          return
        }

        showGenericServerError()
      }
    },
  })
}
